// Axios-based API client that talks directly to the Spring Boot backend.
// Replaces the SvelteKit /api/proxy/... pass-through.
//
// Every outgoing request gets an Auth0 access token attached as
//   Authorization: Bearer <token>
// via a request interceptor (the token is fetched lazily and cached by
// @auth0/auth0-spa-js).

import axios from 'axios';
import { getToken } from './auth.js';
import { handleAuthExpired } from './handleAuthExpired.js';

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002/api/v1';

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use(async (config) => {
  try {
    const token = await getToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (err) {
    // If we can't get a token (e.g. not logged in yet), just send the request
    // without one — the backend will return 401 and the caller can react.
    // eslint-disable-next-line no-console
    console.debug('api: getToken failed, sending request without Authorization', err?.message);
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    // QA-010: any 401 from an /api/* call means the session is invalid.
    // Trigger the central handler (idempotent — multiple parallel 401s
    // converge to a single logout). Still reject so the caller's catch
    // path runs, but the user will be redirected to /login imminently.
    if (err?.response?.status === 401) {
      handleAuthExpired('axios 401 ' + (err.config?.url || ''));
    }
    return Promise.reject(err);
  }
);

// -- helpers -----------------------------------------------------------------

function path(...parts) {
  return parts
    .filter((p) => p !== undefined && p !== null && p !== '')
    .map((p, i) => (i === 0 ? String(p).replace(/\/+$/, '') : String(p).replace(/^\/+|\/+$/g, '')))
    .join('/');
}

async function get(url, config) {
  const r = await api.get(url, config);
  return r.data;
}
async function post(url, body, config) {
  const r = await api.post(url, body, config);
  return r.data;
}
async function put(url, body, config) {
  const r = await api.put(url, body, config);
  return r.data;
}
async function patch(url, body, config) {
  const r = await api.patch(url, body, config);
  return r.data;
}
async function del(url, config) {
  const r = await api.delete(url, config);
  return r.data;
}

// -- typed endpoint groups ---------------------------------------------------
// These mirror the most heavily-used SvelteKit proxy routes. The catch-all
// `request()` helper lets callers hit anything else without adding a method.

export const users = {
  list: (params) => get('/users', { params }),
  get: (id) => get(path('/users', id)),
  create: (body) => post('/users', body),
  update: (id, body) => put(path('/users', id), body),
  remove: (id) => del(path('/users', id)),
  block: (id) => post(path('/users', id, 'block')),
  unblock: (id) => post(path('/users', id, 'unblock')),
  resendPasswordReset: (id) => post(path('/users', id, 'resend-password-reset')),
  organizations: (id) => get(path('/users', id, 'organizations')),
  blockInOrg: (id, orgId) => post(path('/users', id, 'organizations', orgId, 'block')),
  unblockInOrg: (id, orgId) => post(path('/users', id, 'organizations', orgId, 'unblock')),
  config: (id) => get(path('/users', id, 'config')),
  highscore: () => get('/users/highscore')
};

export const studies = {
  list: (params) => get('/studies', { params }),
  get: (id) => get(path('/studies', id)),
  create: (body) => post('/studies', body),
  update: (id, body) => put(path('/studies', id), body),
  remove: (id) => del(path('/studies', id)),
  byProtocol: (protocolNumber) => get(path('/studies/protocolNumber', protocolNumber)),
  validateProtocol: (n) => get(path('/studies/validate/protocolNumber', n)),
  validateName: (n) => get(path('/studies/validate/studyName', n)),
  properties: (studyId) => get(path('/studyProperties', studyId)),
  updateProperties: (studyId, body) => put(path('/studyProperties', studyId), body),
  apiKey: (studyId) => get(path('/studyProperties', studyId, 'api-key')),
  generateApiKey: (studyId) => post(path('/studyProperties', studyId, 'api-key/generate'))
};

export const sites = {
  list: (params) => get('/sites', { params }),
  get: (id) => get(path('/sites', id)),
  // SPTL-486 (round 3): the NG endpoint is /sites/byId/{siteId}?studyGUID=…
  // (path param, matching the Pre-Security Revamp proxy). The old query-param
  // shape (/sites/byId?siteId=…) matches no backend mapping, so the login
  // site pre-fetch always failed and Persons.svelte bounced site users
  // back to /sites.
  byId: ({ siteId, studyGUID }) => get(path('/sites/byId', siteId), { params: { studyGUID } }),
  byIdParam: (siteId) => get(path('/sites/byId', siteId)),
  byStudy: (studyGUID) => get(path('/sites/study', studyGUID)),
  create: (body) => post('/sites', body),
  update: (id, body) => put(path('/sites', id), body),
  remove: (id) => del(path('/sites', id)),
  muted: (id, muted) => put(path('/sites', id, 'muted'), { muted }),
  validateSiteId: (siteId) => get(path('/sites/validate/siteId', siteId)),
  validateSiteName: (siteName) => get(path('/sites/validate/siteName', siteName))
};

export const persons = {
  list: (params) => get('/persons', { params }),
  get: (id) => get(path('/persons', id)),
  create: (body) => post('/persons', body),
  update: (id, body) => put(path('/persons', id), body),
  remove: (id) => del(path('/persons', id)),
  muted: (id, muted) => put(path('/persons', id, 'muted'), { muted }),
  upgradeEligible: (id) => get(path('/persons', id, 'upgradeEligible')),
  setSyncProvider: (id, body) => put(path('/persons', id, 'properties/syncProvider'), body)
};

export const devices = {
  list: (params) => get('/devices', { params }),
  get: (id) => get(path('/devices', id)),
  create: (body) => post('/devices', body),
  update: (id, body) => put(path('/devices', id), body),
  remove: (id) => del(path('/devices', id)),
  readingMetrics: (params) => get('/devices/readingmetrics', { params })
};

export const glucoseReadings = {
  list: (params) => get('/glucoseReadings', { params }),
  count: (params) => get('/glucoseReadings/count', { params }),
  agp: (params) => get('/glucoseReadings/agp', { params }),
  daily: (params) => get('/glucoseReadings/daily', { params }),
  dailyChart: (params) => get('/glucoseReadings/dailyChart', { params }),
  hourlyChart: (params) => get('/glucoseReadings/hourlyChart', { params }),
  deviceCompliance: (params) => get('/glucoseReadings/deviceCompliance', { params }),
  duplicates: (params) => get('/glucoseReadings/duplicates', { params }),
  external: (params) => get('/glucoseReadings/external', { params }),
  studyCompliance: (params) => get('/glucoseReadings/studyCompliance', { params }),
  studyCounts: (params) => get('/glucoseReadings/studyCounts', { params }),
  studySMBGCompliance: (params) => get('/glucoseReadings/studySMBGCompliance', { params }),
  importAbbott: (body) => post('/glucoseReadings/import/abbott', body)
};

export const messages = {
  list: (params) => get('/messages', { params }),
  create: (body) => post('/messages', body),
  markAsRead: (body) => post('/messages/markAsRead', body),
  markSubjectAsRead: (body) => post('/messages/markSubjectAsRead', body),
  studyMessages: (params) => get('/messages/studyMessages', { params })
};

export const phases = {
  list: () => get('/phases'),
  get: (phaseId) => get(path('/phases', phaseId)),
  byStudy: (studyId) => get(path('/phases/study', studyId)),
  create: (body) => post('/phases', body),
  update: (id, body) => put(path('/phases', id), body),
  remove: (id) => del(path('/phases', id))
};

export const schedules = {
  list: (params) => get('/schedules', { params }),
  get: (id) => get(path('/schedules', id)),
  create: (body) => post('/schedules', body),
  update: (id, body) => put(path('/schedules', id), body),
  remove: (id) => del(path('/schedules', id)),
  byStudy: (id) => get(path('/schedules/study', id)),
  byParticipant: (id) => get(path('/schedules/participant', id)),
  schedulesByStudy: (params) => get('/schedules/schedulesByStudy', { params }),
  subjectSchedules: (params) => get('/schedules/subject-schedules', { params }),
  subject: (params) => get('/schedules/subject', { params })
};

export const studySchedules = {
  list: () => get('/studySchedules'),
  get: (id) => get(path('/studySchedules', id)),
  byStudy: (id) => get(path('/studySchedules/study', id)),
  create: (body) => post('/studySchedules', body),
  update: (id, body) => put(path('/studySchedules', id), body),
  remove: (id) => del(path('/studySchedules', id))
};

export const subjectSchedule = {
  list: (params) => get('/subjectSchedule', { params }),
  generate: (body) => post('/subjectSchedule/generate-subject-schedule', body),
  activities: (params) => get('/subjectSchedule/subject-activities', { params }),
  subjectSchedules: (params) => get('/subjectSchedule/subject-schedules', { params }),
  updateActivity: (body) => put('/subjectSchedule/update-subject-activity', body)
};

export const forms = {
  list: (params) => get('/forms', { params }),
  get: (id) => get(path('/forms', id)),
  create: (body) => post('/forms', body),
  update: (id, body) => put(path('/forms', id), body),
  remove: (id) => del(path('/forms', id)),
  types: () => get('/forms/types'),
  languages: () => get('/forms/languages'),
  userForms: (params) => get('/forms/userForms', { params }),
  subjectForms: (params) => get('/forms/subjectForm', { params }),
  subjectForm: (id) => get(path('/forms/subjectForm', id)),
  insulinAggregate: (params) => get('/forms/insulinAggregate', { params })
};

export const roles = {
  list: () => get('/roles'),
  byStudyUser: (studyId) => get(path('/roles/study', studyId, 'user'))
};

export const lookups = {
  byCode: (code) => get(path('/lookups', code)),
  ACTYP: () => get('/lookups/ACTYP'),
  STAT: () => get('/lookups/STAT'),
  SUBACTSTAT: () => get('/lookups/SUBACTSTAT'),
  groups: () => get('/lookupGroups'),
  group: (id) => get(path('/lookupGroups', id)),
  createGroup: (body) => post('/lookupGroups', body),
  updateGroup: (id, body) => put(path('/lookupGroups', id), body),
  removeGroup: (id) => del(path('/lookupGroups', id)),
  types: () => get('/lookupTypes'),
  type: (id) => get(path('/lookupTypes', id)),
  typesByGroup: (groupGuid) => get(path('/lookupTypes/byGroup', groupGuid)),
  createType: (body) => post('/lookupTypes', body),
  updateType: (id, body) => put(path('/lookupTypes', id), body),
  removeType: (id) => del(path('/lookupTypes', id))
};

export const languages = {
  list: () => get('/languages'),
  get: (id) => get(path('/languages', id)),
  create: (body) => post('/languages', body),
  update: (id, body) => put(path('/languages', id), body),
  remove: (id) => del(path('/languages', id)),
  byStudy: (studyId) => get(path('/languages/study', studyId)),
  byStudyLang: (studyId, langId) => get(path('/languages/study', studyId, langId))
};

export const localization = {
  list: (params) => get('/localization', { params }),
  get: (id) => get(path('/localization', id)),
  create: (body) => post('/localization', body),
  update: (id, body) => put(path('/localization', id), body),
  remove: (id) => del(path('/localization', id))
};

export const translations = {
  list: (params) => get('/translations', { params }),
  create: (body) => post('/translations', body),
  update: (id, body) => put(path('/translations', id), body),
  remove: (id) => del(path('/translations', id))
};

export const regulatory = {
  list: () => get('/regulatory'),
  byApp: (app) => get(path('/regulatory', app)),
  adminAll: () => get('/regulatory/admin/all')
};

export const auditLogs = {
  list: (params) => get('/auditLogs', { params })
};

export const logEntries = {
  list: (params) => get('/logEntries', { params })
};

export const eventLog = {
  list: (params) => get('/eventLog', { params })
};

export const onDemandRequests = {
  list: (params) => get('/onDemandRequests', { params }),
  get: (id) => get(path('/onDemandRequests', id)),
  create: (body) => post('/onDemandRequests', body),
  update: (id, body) => put(path('/onDemandRequests', id), body),
  filled: (id) => get(path('/onDemandRequests', id, 'filled')),
  remove: (id) => del(path('/onDemandRequests', id))
};

export const workflows = {
  list: (params) => get('/workflows', { params }),
  get: (id) => get(path('/workflows', id)),
  create: (body) => post('/workflows', body),
  update: (id, body) => put(path('/workflows', id), body),
  remove: (id) => del(path('/workflows', id))
};

export const userWorkflows = {
  list: (params) => get('/userWorkflows', { params }),
  get: (id) => get(path('/userWorkflows', id)),
  create: (body) => post('/userWorkflows', body),
  update: (id, body) => put(path('/userWorkflows', id), body),
  remove: (id) => del(path('/userWorkflows', id)),
  confirmTask: (id, taskId) =>
    post(path('/userWorkflows', id, 'tasks', taskId, 'confirm'))
};

export const phoneInventory = {
  list: (params) => get('/phoneInventory', { params }),
  create: (body) => post('/phoneInventory', body),
  update: (id, body) => put(path('/phoneInventory', id), body),
  remove: (id) => del(path('/phoneInventory', id))
};

export const subjectDevices = {
  list: (params) => get('/subjectDevices', { params })
};

export const subjectSafety = {
  list: (params) => get('/subjectSafety', { params })
};

export const subjectFormProxies = {
  list: (params) => get('/subjectFormProxies', { params }),
  create: (body) => post('/subjectFormProxies', body),
  remove: (id) => del(path('/subjectFormProxies', id))
};

export const sensorLogs = {
  list: (params) => get('/sensorLogs', { params }),
  get: (id) => get(path('/sensorLogs', id))
};

export const enrolmentLog = {
  importLog: (body) => post('/enrolmentLog/import', body)
};

export const stats = {
  list: (params) => get('/stats', { params }),
  allSubjectsWithZeros: (params) => get('/stats/allSubjectsWithZeros', { params }),
  exportGlucoseReadings: (params) => get('/stats/exportGlucoseReadings', { params }),
  importGlucoseReadings: (body) => post('/stats/importGlucoseReadings', body),
  sensor: (params) => get('/stats/sensor', { params }),
  studyCGM: (params) => get('/stats/studyCGMStats', { params }),
  studyParticipantCGM: (params) => get('/stats/studyParticipantCGMStats', { params }),
  subjectCGM: (params) => get('/stats/subjectCGMStats', { params }),
  subjectWithZeros: (params) => get('/stats/subjectWithZeros', { params }),
  timeTravelSensor: (params) => get('/stats/timeTravelSensor', { params }),
  updateMany: (body) => post('/stats/updateMany', body)
};

export const complianceReport = {
  periodStatistics: (params) => get('/complianceReport/periodStatistics', { params }),
  trafficLight: (params) => get('/complianceReport/trafficLight', { params })
};

export const schedulerJobs = {
  list: () => get('/schedulerJobConfigs'),
  get: (jobId) => get(path('/schedulerJobConfigs', jobId)),
  create: (body) => post('/schedulerJobConfigs', body),
  update: (jobId, body) => put(path('/schedulerJobConfigs', jobId), body),
  remove: (jobId) => del(path('/schedulerJobConfigs', jobId)),
  run: (jobId) => post(path('/schedulerJobConfigs', jobId, 'run')),
  availableJobIds: () => get('/schedulerJobConfigs/availableJobIds'),
  clone: (body) => post('/schedulerJobConfigs/clone', body)
};

export const schedulerLogs = {
  list: (params) => get('/schedulerLogs', { params }),
  eventTypes: () => get('/schedulerLogs/eventTypes')
};

export const schedulerRuntime = {
  status: () => get('/schedulerRuntime'),
  start: () => post('/schedulerRuntime/start'),
  stop: () => post('/schedulerRuntime/stop')
};

export const dataManagement = {
  status: () => get('/data-management'),
  cleanup: (body) => post('/data-management/cleanup', body),
  cleanupPreview: (params) => get('/data-management/cleanup/preview', { params }),
  exportStart: (body) => post('/data-management/export', body),
  exportPreflight: (params) => get('/data-management/export/preflight', { params }),
  exportStatus: (jobId) => get(path('/data-management/export/status', jobId)),
  importStart: (body) => post('/data-management/import', body)
};

export const cgm = {
  importData: (body) => post('/cgm/import/data', body),
  importDataStatus: (deviceEventLogGuid) =>
    get(path('/cgm/import/data/status', deviceEventLogGuid))
};

export const weight = {
  list: (params) => get('/weight', { params })
};

export const siteCgmExports = {
  list: (params) => get('/site-cgm-exports', { params }),
  download: (jobId) => get(path('/site-cgm-exports/download', jobId), { responseType: 'blob' }),
  status: (jobId) => get(path('/site-cgm-exports/status', jobId))
};

export const files = {
  upload: (body, config) => post('/files/upload', body, config),
  download: (params, config) => get('/files/download', { ...config, params, responseType: 'blob' }),
  remove: (body) => post('/files/delete', body)
};

export const search = {
  subject: (params) => get('/search/subject', { params })
};

export const permissions = {
  check: (body) => post('/permissions/check', body)
};

export const activities = {
  list: (params) => get('/activities', { params }),
  get: (id) => get(path('/activities', id)),
  bySchedule: (params) => get('/activities/bySchedule', { params })
};

export const authApi = {
  profile: () => get('/auth/profile'),
  createUser: (body) => post('/auth/create-user', body),
  invite: (body) => post('/auth/invite', body)
};

export const db = {
  list: () => get('/db/list'),
  switchTo: (body) => post('/db/switch', body)
};

// Catch-all helper for endpoints not covered above.
export function request(method, url, opts = {}) {
  return api.request({ method, url, ...opts }).then((r) => r.data);
}

export default api;
